import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import {
  FileText, Check, X, Plus, Trash2, ArrowLeft, Loader2,
  Package, Layers, Video, Globe, LayoutDashboard, AlertCircle,
  ChevronRight, Save, RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";

const MODULE_CONFIG = {
  visualization: { label: "Visualization", icon: Layers, color: "bg-blue-500", desc: "3D renderings and floorplans" },
  marketing: { label: "Marketing", icon: LayoutDashboard, color: "bg-pink-500", desc: "Branding and collateral" },
  animation: { label: "Animation", icon: Video, color: "bg-purple-500", desc: "Video and motion content" },
  vexos: { label: "VEXOS", icon: Globe, color: "bg-cyan-500", desc: "360 tours and interactive" },
  hosting: { label: "Hosting", icon: Package, color: "bg-amber-500", desc: "Website and maintenance" },
};

const DELIVERABLE_TYPES = [
  "Floorplan", "Rendering", "Interior Rendering", "Exterior Rendering",
  "360 Tour", "Animation", "Video", "Cinematic", "3D Model", 
  "Penthouse Rendering", "Hotspot", "Amenity Rendering"
];

function ModuleCard({ module, onToggle, onUpdate }) {
  const config = MODULE_CONFIG[module.module_type] || { label: module.module_type, icon: Package, color: "bg-gray-500" };
  const Icon = config.icon;
  
  return (
    <div className={`flex items-center justify-between p-4 rounded-lg border ${module.is_included ? "bg-primary/5 border-primary/30" : "bg-muted/30 border-transparent"}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-medium">{config.label}</p>
          <p className="text-xs text-muted-foreground">{config.desc}</p>
        </div>
      </div>
      <Switch checked={module.is_included} onCheckedChange={() => onToggle(module.module_type)} />
    </div>
  );
}

function DeliverableRow({ item, index, onUpdate, onRemove }) {
  return (
    <div className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-muted/30 group">
      <div className="flex-1 grid grid-cols-3 gap-3 items-center">
        <Input 
          value={item.deliverable_type} 
          onChange={e => onUpdate(index, "deliverable_type", e.target.value)}
          placeholder="Type"
          className="h-9"
        />
        <Input 
          type="number" 
          value={item.quantity} 
          onChange={e => onUpdate(index, "quantity", parseInt(e.target.value) || 0)}
          min={1}
          className="h-9 w-20"
        />
        <Input 
          value={item.description || ""} 
          onChange={e => onUpdate(index, "description", e.target.value)}
          placeholder="Description (optional)"
          className="h-9"
        />
      </div>
      <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100" onClick={() => onRemove(index)}>
        <Trash2 className="w-4 h-4 text-destructive" />
      </Button>
    </div>
  );
}

export default function ScopeConfirmation() {
  const { id: programId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  
  const [modules, setModules] = useState([
    { module_type: "visualization", is_included: true, description: "" },
    { module_type: "marketing", is_included: false, description: "" },
    { module_type: "animation", is_included: false, description: "" },
    { module_type: "vexos", is_included: false, description: "" },
    { module_type: "hosting", is_included: false, description: "" },
  ]);
  
  const [deliverables, setDeliverables] = useState([]);
  const [scopeStatus, setScopeStatus] = useState("pending_extraction");
  const [extractionError, setExtractionError] = useState(null);

  useEffect(() => {
    if (programId) {
      fetchProgram();
      fetchScopeStatus();
    }
  }, [programId]);

  async function fetchProgram() {
    try {
      const res = await apiClient.get(`/programs/${programId}`);
      setProgram(res.data);
    } catch (e) {
      toast.error("Failed to load program");
    }
  }

  async function fetchScopeStatus() {
    try {
      const res = await apiClient.get(`/programs/${programId}/scope-status`);
      setScopeStatus(res.data.scope_status);
      
      if (res.data.scope_status === "confirmed") {
        toast.info("Scope already confirmed for this program");
      }
    } catch (e) {
      // Scope status endpoint might not exist for older programs
    } finally {
      setLoading(false);
    }
  }

  async function handleExtractScope(forceFresh = false) {
    setExtracting(true);
    setExtractionError(null);

    try {
      // When the user clicks Re-Extract (scope already extracted once),
      // force=true bypasses the cached parsed_data and re-runs the AI.
      // This ensures the latest parser improvements take effect.
      const shouldForce = forceFresh || scopeStatus === "extracted";
      const url = `/programs/${programId}/extract-scope${shouldForce ? "?force=true" : ""}`;
      const res = await apiClient.post(url);
      
      if (res.data.success) {
        // Update modules
        if (res.data.modules?.length > 0) {
          setModules(res.data.modules.map(m => ({
            module_type: m.module_type,
            is_included: m.is_included,
            description: m.description || ""
          })));
        }
        
        // Update deliverables
        if (res.data.deliverables?.length > 0) {
          setDeliverables(res.data.deliverables.map(d => ({
            deliverable_type: d.deliverable_type || d,
            quantity: d.quantity || 1,
            description: d.description || ""
          })));
        }
        
        setScopeStatus("extracted");
        toast.success("Scope extracted from agreement!");
      } else {
        setExtractionError(res.data.error_message);
        toast.error(res.data.error_message || "Failed to extract scope");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to extract scope";
      setExtractionError(msg);
      toast.error(msg);
    } finally {
      setExtracting(false);
    }
  }

  function toggleModule(moduleType) {
    setModules(prev => prev.map(m => 
      m.module_type === moduleType ? { ...m, is_included: !m.is_included } : m
    ));
  }

  function updateDeliverable(index, field, value) {
    setDeliverables(prev => prev.map((d, i) => 
      i === index ? { ...d, [field]: value } : d
    ));
  }

  function removeDeliverable(index) {
    setDeliverables(prev => prev.filter((_, i) => i !== index));
  }

  function addDeliverable() {
    setDeliverables(prev => [...prev, { deliverable_type: "", quantity: 1, description: "" }]);
  }

  async function handleConfirmScope() {
    const validDeliverables = deliverables.filter(d => d.deliverable_type && d.quantity > 0);
    
    if (validDeliverables.length === 0) {
      toast.error("Please add at least one deliverable");
      return;
    }
    
    setConfirming(true);
    
    try {
      const res = await apiClient.post(`/programs/${programId}/confirm-scope`, {
        modules: modules.filter(m => m.is_included),
        deliverables: validDeliverables
      });
      
      if (res.data.success) {
        toast.success("Scope confirmed!", {
          description: `${res.data.milestones_created} milestones created`
        });
        navigate(`/programs/${programId}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to confirm scope");
    } finally {
      setConfirming(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const includedModulesCount = modules.filter(m => m.is_included).length;
  const totalDeliverables = deliverables.reduce((sum, d) => sum + (d.quantity || 0), 0);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-light tracking-tight">Extract & Confirm Scope</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {program?.program_name || "Program"}
            </p>
          </div>
        </div>
        
        {/* Status Badge */}
        <Badge variant="outline" className={
          scopeStatus === "confirmed" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" :
          scopeStatus === "extracted" ? "bg-blue-500/10 text-blue-600 border-blue-500/20" :
          scopeStatus === "extraction_failed" ? "bg-red-500/10 text-red-600 border-red-500/20" :
          "bg-amber-500/10 text-amber-600 border-amber-500/20"
        }>
          {scopeStatus === "confirmed" ? "Scope Confirmed" :
           scopeStatus === "extracted" ? "Scope Extracted" :
           scopeStatus === "extraction_failed" ? "Extraction Failed" :
           "Scope Not Confirmed"}
        </Badge>
      </div>

      {/* Step 1: Extract */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">1</div>
              <div>
                <CardTitle className="text-lg">Parse Agreement</CardTitle>
                <CardDescription>Extract scope and deliverables from the signed agreement</CardDescription>
              </div>
            </div>
            <Button onClick={handleExtractScope} disabled={extracting || scopeStatus === "confirmed"}>
              {extracting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extracting...</>
              ) : scopeStatus === "extracted" ? (
                <><RefreshCw className="w-4 h-4 mr-2" /> Re-Extract</>
              ) : (
                <><FileText className="w-4 h-4 mr-2" /> Extract Scope</>
              )}
            </Button>
          </div>
        </CardHeader>
        {extractionError && (
          <CardContent className="pt-0">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              <AlertCircle className="w-4 h-4" />
              {extractionError}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Step 2: Review Modules */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">2</div>
            <div>
              <CardTitle className="text-lg">Modules Included</CardTitle>
              <CardDescription>Toggle which service modules are included in this scope</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {modules.map(m => (
              <ModuleCard key={m.module_type} module={m} onToggle={toggleModule} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Step 3: Review Deliverables */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">3</div>
              <div>
                <CardTitle className="text-lg">Deliverables</CardTitle>
                <CardDescription>Review and edit deliverables - these will become milestones</CardDescription>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={addDeliverable}>
              <Plus className="w-4 h-4 mr-1" /> Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {deliverables.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No deliverables extracted yet</p>
              <p className="text-xs mt-1">Click "Extract Scope" above or add items manually</p>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="grid grid-cols-3 gap-3 px-3 py-2 text-xs font-medium text-muted-foreground uppercase">
                <span>Type</span>
                <span>Qty</span>
                <span>Description</span>
              </div>
              <Separator />
              {deliverables.map((item, idx) => (
                <DeliverableRow key={idx} item={item} index={idx} onUpdate={updateDeliverable} onRemove={removeDeliverable} />
              ))}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex items-center justify-between border-t bg-muted/30">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-muted-foreground">{includedModulesCount} modules</span>
            <span className="text-muted-foreground">{deliverables.length} line items</span>
            <span className="font-medium">{totalDeliverables} total deliverables</span>
          </div>
        </CardFooter>
      </Card>

      {/* Step 4: Confirm */}
      <Card className="border-primary/30 bg-primary/5">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-sm font-bold text-primary-foreground">4</div>
            <div>
              <CardTitle className="text-lg">Confirm Scope & Create Milestones</CardTitle>
              <CardDescription>This will create calendar milestones and initialize the program tracker</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardFooter>
          <Button 
            size="lg" 
            onClick={handleConfirmScope} 
            disabled={confirming || deliverables.length === 0 || scopeStatus === "confirmed"}
            className="w-full"
          >
            {confirming ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Confirming...</>
            ) : scopeStatus === "confirmed" ? (
              <><Check className="w-4 h-4 mr-2" /> Scope Already Confirmed</>
            ) : (
              <><Check className="w-4 h-4 mr-2" /> Confirm Scope & Create Milestones</>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
